import {
  applyCoachProfileChanges,
  COACH_PROFILE_SECTIONS,
  type CoachProfileChange,
  CoachProfileEditor,
  CoachProfileSectionNav,
  coachProfileSectionSpy,
  type CoachProfileValidationFailure,
  CoachProfileView,
  diffCoachProfiles,
  formatProfilePhotoCommitError,
  markCoachProfileDirty,
  type ProfilePhotoEditSession,
  pruneEmptyCoachProfileFragments,
  validateCoachProfile,
} from '@efdigital/ll-addon-coaching';
import type PicklistsService from '@efdigital/ll-addon-content/services/picklists';
import type { CoachProfile } from '@efdigital/ll-addon-data';
import { DataTypeCoaching } from '@efdigital/ll-addon-data/data-types';
import {
  COACHING_COUNTRIES_GROUP,
  COACHING_INDUSTRIES_GROUP,
  COACHING_SENIORITY_GROUP,
  COACHING_SPECIALISMS_GROUP,
  COACHING_TYPES_GROUP,
  createCoachProfile,
  updateCoachProfile,
} from '@efdigital/ll-xceed-v2/data/builders/coaching/coach-profiles';
import { accessibleScrollBehavior } from '@efdigital/ll-xceed-v2/utils/application/scroll';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';

type SavePhase = 'idle' | 'uploading-photo' | 'saving';

interface EditMyProfileSignature {
  Args: {
    /** The coach's live, published profile. */
    published: CoachProfile;
    /** Their in-flight change request awaiting review, if any. */
    pending: CoachProfile | null;
    /**
     * Called after a successful submit, so the host can react (e.g. refresh
     * the route). Keeps this component from needing to know about routing.
     */
    onSaved?: () => void | Promise<void>;
  };
  Element: HTMLDivElement;
}

/**
 * A coach's self-service view of their own profile. Read-only by default;
 * "Edit" opens the shared CoachProfileEditor in `self` mode against a scratch
 * draft. Saving submits a `pending_review` change request (a new record, or
 * an update to an existing pending one) — the published profile is never
 * touched directly, so the edit lands in the admin approval queue rather than
 * going live immediately.
 */
export default class EditMyProfile extends Component<EditMyProfileSignature> {
  @service declare store: Store;
  @service declare picklists: PicklistsService;

  @tracked mode: 'view' | 'edit' = 'view';
  /** Scratch record edited in the form; copied onto/into a saved record. */
  @tracked workingCopy: CoachProfile | null = null;
  @tracked saving = false;
  @tracked saveError: string | null = null;
  @tracked confirming = false;
  @tracked confirmError: string | null = null;
  @tracked validationFailures: CoachProfileValidationFailure[] = [];
  @tracked activeSection = COACH_PROFILE_SECTIONS[0]?.id ?? 'identity';
  @tracked savePhase: SavePhase = 'idle';
  @tracked photoEditSession: ProfilePhotoEditSession | null = null;

  setPhotoEditSession = (session: ProfilePhotoEditSession | null): void => {
    this.photoEditSession = session;
  };

  // Set while a click-triggered smooth-scroll is in flight, so the section
  // spy doesn't fight it and flicker through intermediate sections.
  #suppressSpyUntil = 0;
  #resumeSpyTimeout: ReturnType<typeof setTimeout> | null = null;

  // Picklist options come from the picklists service (the public, cached
  // /end-user/picklists/group-items endpoint) — no read:picklists right needed.
  get specialtyOptions() {
    return this.picklists.groups[COACHING_SPECIALISMS_GROUP]?.items ?? [];
  }

  // Locations stay free text (omitted) — the editor falls back to tag entry.
  get positioningOptions() {
    return {
      countries: this.picklists.groups[COACHING_COUNTRIES_GROUP]?.items ?? [],
      coachingTypes: this.picklists.groups[COACHING_TYPES_GROUP]?.items ?? [],
      industries: this.picklists.groups[COACHING_INDUSTRIES_GROUP]?.items ?? [],
      levelsOfSeniority:
        this.picklists.groups[COACHING_SENIORITY_GROUP]?.items ?? [],
    };
  }

  get isEditing(): boolean {
    return this.mode === 'edit';
  }

  get hasPending(): boolean {
    return Boolean(this.args.pending);
  }

  /** What the coach has changed in their awaiting-review submission. */
  get pendingChanges(): CoachProfileChange[] {
    return this.args.pending
      ? diffCoachProfiles(this.args.published, this.args.pending)
      : [];
  }

  get editLabel(): string {
    return this.hasPending ? 'Continue editing changes' : 'Edit profile';
  }

  get submitLabel(): string {
    if (this.savePhase === 'uploading-photo') {
      return 'Uploading photo…';
    }

    if (this.savePhase === 'saving') {
      return 'Submitting…';
    }

    return 'Submit';
  }

  get isSaving(): boolean {
    return this.saving || this.savePhase !== 'idle';
  }

  startEdit = () => {
    const draft = this.store.createRecord<CoachProfile>(
      DataTypeCoaching.CoachProfile,
      {
        user: this.args.published.user,
        status: 'pending_review',
      }
    );

    // Seed from the pending submission if one exists, else the published
    // profile — applyCoachProfileChanges deep-clones arrays and fragments, so
    // the draft never aliases the source record.
    applyCoachProfileChanges(draft, this.args.pending ?? this.args.published);

    this.workingCopy = draft;
    this.validationFailures = [];
    this.saveError = null;
    this.activeSection = COACH_PROFILE_SECTIONS[0]?.id ?? 'identity';
    this.mode = 'edit';
  };

  cancelEdit = () => {
    this.photoEditSession?.reset();
    // Rolling back a never-saved createRecord unloads it from the store.
    this.workingCopy?.rollbackAttributes();
    this.workingCopy = null;
    this.validationFailures = [];
    this.saveError = null;
    this.mode = 'view';
  };

  /** Whether the coach still needs to confirm (not confirmed, and no
   * confirmation already awaiting review). */
  get showConfirmPrompt(): boolean {
    return (
      !this.args.published.profileConfirmed &&
      this.args.pending?.profileConfirmed !== true
    );
  }

  /**
   * Confirm the profile. The API doesn't let coaches edit their published
   * record, so — like any other change — the confirmation is submitted as a
   * `pending_review` change request (folded onto an existing one if present).
   * Onboarding step 2 completes once an administrator approves it.
   */
  confirmProfile = async () => {
    if (this.confirming || !this.showConfirmPrompt) {
      return;
    }

    this.confirming = true;
    this.confirmError = null;

    try {
      const pending = this.args.pending;

      if (pending) {
        // Add the confirmation to the existing change request.
        pending.profileConfirmed = true;
        markCoachProfileDirty(pending);
        try {
          await this.store.request(updateCoachProfile(pending, this.store));
        } catch (error) {
          pending.rollbackAttributes();
          throw error;
        }
      } else {
        // No change request yet: create one that mirrors the published profile
        // and just flips the confirmation flag.
        const draft = this.store.createRecord<CoachProfile>(
          DataTypeCoaching.CoachProfile,
          { user: this.args.published.user, status: 'pending_review' }
        );
        applyCoachProfileChanges(draft, this.args.published);
        draft.profileConfirmed = true;
        markCoachProfileDirty(draft);
        try {
          await this.store.request(createCoachProfile(draft, this.store));
        } catch (error) {
          draft.rollbackAttributes();
          throw error;
        }
      }

      await this.args.onSaved?.();
    } catch (error: unknown) {
      this.confirmError =
        error instanceof Error
          ? error.message
          : 'Could not submit your confirmation. Please try again.';
    } finally {
      this.confirming = false;
    }
  };

  submitChanges = async () => {
    const draft = this.workingCopy;

    if (!draft || this.saving) {
      return;
    }

    this.saveError = null;
    pruneEmptyCoachProfileFragments(draft);

    const failures = validateCoachProfile(draft, { mode: 'self' });

    if (failures.length > 0) {
      this.validationFailures = failures;
      this.scrollToSectionId(failures[0]!.sectionId);
      return;
    }

    this.validationFailures = [];
    this.saving = true;

    try {
      if (this.photoEditSession?.hasChanges()) {
        this.savePhase = 'uploading-photo';
        try {
          await this.photoEditSession.commit(draft);
        } catch (error) {
          throw new Error(formatProfilePhotoCommitError(error), {
            cause: error,
          });
        }
      }

      this.savePhase = 'saving';

      const pending = this.args.pending;

      if (pending) {
        // Fold the draft onto the existing change request and update it.
        applyCoachProfileChanges(pending, draft);
        markCoachProfileDirty(pending);
        try {
          await this.store.request(updateCoachProfile(pending, this.store));
        } catch (error) {
          // Revert the in-place mutation so a failed save doesn't leave the
          // pending record dirty / showing changes that never persisted.
          pending.rollbackAttributes();
          throw error;
        }
        draft.rollbackAttributes();
      } else {
        // First submission: the draft itself becomes the pending record.
        markCoachProfileDirty(draft);
        await this.store.request(createCoachProfile(draft, this.store));
      }

      this.workingCopy = null;
      this.mode = 'view';
      await this.args.onSaved?.();
    } catch (error: unknown) {
      this.saveError =
        error instanceof Error
          ? error.message
          : 'Something went wrong submitting your changes.';
    } finally {
      this.saving = false;
      this.savePhase = 'idle';
    }
  };

  scrollToSection = (sectionId: string, event: Event) => {
    event.preventDefault();
    this.#pauseSpyDuringScroll();
    this.scrollToSectionId(sectionId);
  };

  scrollToSectionId = (sectionId: string) => {
    this.activeSection = sectionId;
    document.getElementById(`coach-section-${sectionId}`)?.scrollIntoView({
      behavior: accessibleScrollBehavior(),
      block: 'start',
    });
  };

  // Highlight the section the spy reports, unless a click-triggered
  // smooth-scroll is mid-flight (then keep the clicked target highlighted).
  handleSpyActiveSection = (sectionId: string) => {
    if (Date.now() < this.#suppressSpyUntil) {
      return;
    }

    this.activeSection = sectionId;
  };

  /**
   * Suppresses the auto-highlight while a click-triggered smooth-scroll is in
   * flight, so it doesn't flicker through the sections it passes on the way.
   * Resumes on the native `scrollend` event where supported, with a flat
   * timeout fallback.
   */
  #pauseSpyDuringScroll = () => {
    this.#suppressSpyUntil = Date.now() + 800;

    if (this.#resumeSpyTimeout) {
      clearTimeout(this.#resumeSpyTimeout);
    }

    this.#resumeSpyTimeout = setTimeout(() => {
      this.#suppressSpyUntil = 0;
    }, 800);

    if ('onscrollend' in window) {
      window.addEventListener(
        'scrollend',
        () => {
          this.#suppressSpyUntil = 0;

          if (this.#resumeSpyTimeout) {
            clearTimeout(this.#resumeSpyTimeout);
            this.#resumeSpyTimeout = null;
          }
        },
        { once: true }
      );
    }
  };

  <template>
    <div ...attributes>
      {{#if this.isEditing}}
        <div class="row g-4 gx-lg-4">
          <aside class="col-12 col-lg-4">
            {{! sticky lives on a wrapper: .card sets position:relative and
                wins over .sticky-lg-top in this theme's import order }}
            <div
              class="ll-coach-profile-sticky-nav sticky-lg-top py-lg-3 overflow-y-auto"
            >
              <div class="card border shadow-sm rounded-4 p-3 p-lg-4">
                <button
                  type="button"
                  class="btn btn-link p-0 mb-2 small text-body-secondary text-decoration-none d-inline-flex align-items-center gap-1"
                  disabled={{this.saving}}
                  {{on "click" this.cancelEdit}}
                >
                  <iconify-icon
                    inline
                    icon="ph:caret-left"
                    aria-hidden="true"
                  />
                  Back to profile
                </button>
                <h2 class="h5 fw-bold mb-2">Edit your profile</h2>

                <hr class="my-2" />

                <CoachProfileSectionNav
                  @activeSection={{this.activeSection}}
                  @onSelect={{this.scrollToSection}}
                />

                <hr class="my-2" />

                {{#if this.saveError}}
                  <div class="alert alert-danger small p-2" role="alert">
                    {{this.saveError}}
                  </div>
                {{/if}}

                {{#if this.validationFailures.length}}
                  <div class="alert alert-danger small p-2" role="alert">
                    {{#each this.validationFailures as |failure|}}
                      <p class="mb-1">{{failure.message}}</p>
                    {{/each}}
                  </div>
                {{/if}}

                <div class="d-flex gap-2 justify-content-center">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    disabled={{this.isSaving}}
                    {{on "click" this.cancelEdit}}
                  >Cancel</button>
                  <button
                    type="button"
                    class="btn btn-sm btn-primary text-nowrap"
                    disabled={{this.isSaving}}
                    aria-busy={{this.isSaving}}
                    {{on "click" this.submitChanges}}
                  >
                    {{#if this.isSaving}}
                      <span
                        class="spinner-border spinner-border-sm me-1"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      {{this.submitLabel}}
                    {{else}}
                      Submit
                    {{/if}}
                  </button>
                </div>
              </div>
            </div>
          </aside>

          <div class="col-12 col-lg-8">
            {{#if this.workingCopy}}
              <div {{coachProfileSectionSpy this.handleSpyActiveSection}}>
                <CoachProfileEditor
                  @coach={{this.workingCopy}}
                  @mode="self"
                  @specialtyOptions={{this.specialtyOptions}}
                  @positioningOptions={{this.positioningOptions}}
                  @onPhotoEditSessionChange={{this.setPhotoEditSession}}
                />
              </div>
            {{/if}}
          </div>
        </div>
      {{else}}
        {{#if this.hasPending}}
          <div class="alert alert-info d-flex flex-column gap-2" role="status">
            <div class="d-flex align-items-start gap-2">
              <iconify-icon
                inline
                icon="ph:clock-countdown"
                class="fs-5 flex-shrink-0"
                aria-hidden="true"
              />
              <div>
                <p class="fw-semibold mb-1">Your changes are awaiting review.</p>
                <p class="small mb-0">
                  You'll keep seeing your current live profile below until an
                  administrator approves your requested changes.
                </p>
              </div>
            </div>
            {{#if this.pendingChanges.length}}
              <div class="small">
                Pending updates:
                {{#each this.pendingChanges as |change index|}}
                  {{if index ", "}}<span
                    class="fw-semibold"
                  >{{change.label}}</span>
                {{/each}}
              </div>
            {{/if}}
          </div>
        {{/if}}

        <CoachProfileView class="ll-coach-profile-view" @coach={{@published}}>
          <:actions>
            <button
              type="button"
              class="btn btn-outline-primary d-flex mx-auto align-items-center gap-2 mt-3
                {{unless this.showConfirmPrompt 'mb-4'}}"
              {{on "click" this.startEdit}}
            >
              <iconify-icon inline icon="ph:pencil-simple" aria-hidden="true" />
              {{this.editLabel}}
            </button>

            {{#if this.showConfirmPrompt}}
              <div
                class="card border-primary border-2 shadow-sm rounded-4 mt-4 mb-4"
              >
                <div
                  class="card-body d-flex flex-column flex-sm-row align-items-sm-center gap-3"
                >
                  <div class="flex-grow-1">
                    <p class="fw-bold mb-1">Confirm your profile to finish
                      onboarding</p>
                    <p class="small text-body-secondary mb-0">Check the details
                      below are correct, then confirm. We'll review it, and then
                      you can be matched with coachees.</p>
                  </div>
                  <button
                    type="button"
                    class="btn btn-primary d-inline-flex align-items-center gap-2 flex-shrink-0"
                    disabled={{this.confirming}}
                    aria-busy={{this.confirming}}
                    {{on "click" this.confirmProfile}}
                  >
                    {{#if this.confirming}}
                      <span
                        class="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Submitting…
                    {{else}}
                      <iconify-icon
                        inline
                        icon="ph:check-circle"
                        aria-hidden="true"
                      />
                      Confirm my profile
                    {{/if}}
                  </button>
                </div>
              </div>
              {{#if this.confirmError}}
                <div
                  class="alert alert-danger small p-2 mt-2 mb-0"
                  role="alert"
                >
                  {{this.confirmError}}
                </div>
              {{/if}}
            {{/if}}
          </:actions>
        </CoachProfileView>
      {{/if}}
    </div>
  </template>
}
